import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Modal } from '@/components/primitives/Modal'
import { Spinner } from '@/components/feedback/Spinner'
import { listResidents, createResident, updateResident, deleteResident, type Resident } from './api'
import { listFacilities } from '@/features/facilities/api'
import { useAuth } from '@/features/auth/AuthContext'
import { useFacilityFilter } from '@/store/facilityFilterStore'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { isValidFax, formatPhoneInput } from '@/lib/phone'
import './PatientsPCPsDrawer.css'

function parseName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first_name: '', last_name: '' }
  if (parts.length === 1) return { first_name: parts[0]!, last_name: '' }
  const last = parts.pop()!
  return { first_name: parts.join(' '), last_name: last }
}

function displayName(r: Resident): string {
  return [r.first_name, r.last_name].filter(Boolean).join(' ')
}

function formatFax(value: string): string {
  return formatPhoneInput(value)
}

interface RowForm { name: string; pcp_name: string; pcp_fax: string; faxErr: string }
const emptyRow = (): RowForm => ({ name: '', pcp_name: '', pcp_fax: '', faxErr: '' })

interface Props {
  open: boolean
  onClose: () => void
}

export function PatientsPCPsDrawer({ open, onClose }: Props): React.JSX.Element {
  const { user } = useAuth()
  const { facilityId: filterFacilityId } = useFacilityFilter()
  const activeFacilityId = user?.role === 'SUPER_ADMIN' ? filterFacilityId : (user?.facility_id ?? null)
  const qc = useQueryClient()
  const toast = useToast()

  const [showAddRow, setShowAddRow] = useState(false)
  const [addForm, setAddForm] = useState<RowForm>(emptyRow)
  const [addFacilityId, setAddFacilityId] = useState('')
  const [editId, setEditId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<RowForm>(emptyRow)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const addNameRef = useRef<HTMLInputElement>(null)
  const editNameRef = useRef<HTMLInputElement>(null)

  // SUPER_ADMIN viewing "All Facilities" has no active facility — they must
  // choose which facility a new resident belongs to.
  const needsFacilityPick = !activeFacilityId
  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: listFacilities,
    enabled: open && needsFacilityPick,
    staleTime: 60_000,
  })
  const facilityMap = new Map(facilities.map((f) => [f.id, f.name]))
  const colCount = needsFacilityPick ? 4 : 3

  const { data: residents = [], isLoading } = useQuery({
    queryKey: ['residents', activeFacilityId],
    queryFn: () => listResidents({ page_size: 200, page: 0, facility_id: activeFacilityId ?? undefined }),
    enabled: open,
    staleTime: 30_000,
  })
  const confirmResident = residents.find((r) => r.id === confirmDeleteId)

  useEffect(() => {
    if (showAddRow) setTimeout(() => addNameRef.current?.focus(), 50)
  }, [showAddRow])

  useEffect(() => {
    if (editId) setTimeout(() => editNameRef.current?.focus(), 50)
  }, [editId])

  const createMut = useMutation({
    mutationFn: (form: RowForm) => {
      const { first_name, last_name } = parseName(form.name)
      return createResident({
        facility_id: (activeFacilityId ?? addFacilityId)!,
        first_name: first_name || 'Unknown',
        last_name,
        pcp_name: form.pcp_name.trim() || null,
        pcp_fax: form.pcp_fax.trim() || null,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['residents'] })
      setShowAddRow(false)
      setAddForm(emptyRow)
      setAddFacilityId('')
    },
    onError: (err) => toast.error('Could not add resident', err instanceof ApiError ? err.message : 'Try again in a moment.'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, form }: { id: string; form: RowForm }) => {
      const { first_name, last_name } = parseName(form.name)
      return updateResident(id, {
        first_name: first_name || 'Unknown',
        last_name,
        pcp_name: form.pcp_name.trim() || null,
        pcp_fax: form.pcp_fax.trim() || null,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['residents'] })
      setEditId(null)
      setEditForm(emptyRow)
    },
    onError: () => toast.error('Could not update resident', 'Try again in a moment.'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteResident(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['residents'] })
      setEditId(null)
      setConfirmDeleteId(null)
    },
    onError: () => toast.error('Could not delete resident', 'Try again in a moment.'),
  })

  function startEdit(r: Resident) {
    setShowAddRow(false)
    setEditId(r.id)
    setEditForm({ name: displayName(r), pcp_name: r.pcp_name ?? '', pcp_fax: r.pcp_fax ?? '', faxErr: '' })
  }

  function validateAndSaveAdd() {
    if (!addForm.name.trim()) return
    if (!parseName(addForm.name).last_name) {
      toast.error('Enter full name', "Please enter the resident's first and last name.")
      return
    }
    if (needsFacilityPick && !addFacilityId) {
      toast.error('Pick a facility', 'Choose which facility this resident belongs to.')
      return
    }
    if (addForm.pcp_fax && !isValidFax(addForm.pcp_fax)) {
      setAddForm((f) => ({ ...f, faxErr: 'Invalid fax' }))
      return
    }
    createMut.mutate(addForm)
  }

  function validateAndSaveEdit() {
    if (!editForm.name.trim()) return
    if (!parseName(editForm.name).last_name) {
      toast.error('Enter full name', "Please enter the resident's first and last name.")
      return
    }
    if (editForm.pcp_fax && !isValidFax(editForm.pcp_fax)) {
      setEditForm((f) => ({ ...f, faxErr: 'Invalid fax' }))
      return
    }
    updateMut.mutate({ id: editId!, form: editForm })
  }

  function handleAddKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') { e.preventDefault(); validateAndSaveAdd() }
    if (e.key === 'Escape') { setShowAddRow(false); setAddForm(emptyRow) }
  }

  function handleEditKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') { e.preventDefault(); validateAndSaveEdit() }
    if (e.key === 'Escape') { setEditId(null); setEditForm(emptyRow) }
  }

  function handleAddResident() {
    setEditId(null)
    setShowAddRow(true)
    setAddForm(emptyRow)
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Patients & PCPs"
      subtitle={`Manage residents and where their vitals are faxed · ${residents.length} resident${residents.length !== 1 ? 's' : ''}`}
      footer={
        <Button onClick={onClose} leftIcon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}>Done</Button>
      }
    >
      <div className="pcp-drawer">
        {isLoading ? (
          <div className="pcp-loading"><Spinner /></div>
        ) : (
          <div style={{ padding: '12px 0' }}>
            <table className="pcp-table">
              <thead>
                <tr>
                  <th className="pcp-th">Resident</th>
                  {needsFacilityPick && <th className="pcp-th">Facility</th>}
                  <th className="pcp-th">PCP</th>
                  <th className="pcp-th pcp-th--fax">Fax</th>
                  <th className="pcp-th pcp-th--actions" />
                </tr>
              </thead>
              <tbody>
                {residents.length === 0 && !showAddRow && (
                  <tr>
                    <td colSpan={colCount} className="pcp-td pcp-empty-msg">
                      No residents yet. Add a resident below or import a CSV.
                    </td>
                  </tr>
                )}

                {residents.map((r) => {
                  if (editId === r.id) {
                    return (
                      <tr key={r.id} className="pcp-row pcp-row-edit">
                        <td className="pcp-td">
                          <input
                            ref={editNameRef}
                            className="pcp-inline-input"
                            placeholder="Resident name"
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                            onKeyDown={handleEditKey}
                          />
                        </td>
                        {needsFacilityPick && (
                          <td className="pcp-td">
                            <span className="pcp-facility-name">{facilityMap.get(r.facility_id) ?? '—'}</span>
                          </td>
                        )}
                        <td className="pcp-td">
                          <input
                            className="pcp-inline-input"
                            placeholder="PCP name"
                            value={editForm.pcp_name}
                            onChange={(e) => setEditForm((f) => ({ ...f, pcp_name: e.target.value }))}
                            onKeyDown={handleEditKey}
                          />
                        </td>
                        <td className="pcp-td pcp-td-fax-edit">
                          <div className="pcp-fax-edit-row">
                            <div className="pcp-fax-input-wrap">
                              <input
                                className={'pcp-inline-input' + (editForm.faxErr ? ' pcp-inline-input--err' : '')}
                                placeholder="Fax number"
                                value={editForm.pcp_fax}
                                onChange={(e) => {
                                  const v = formatFax(e.target.value)
                                  setEditForm((f) => ({ ...f, pcp_fax: v, faxErr: '' }))
                                }}
                                onKeyDown={handleEditKey}
                              />
                              {editForm.faxErr && <span className="pcp-fax-err">{editForm.faxErr}</span>}
                            </div>
                            <button
                              type="button"
                              className="pcp-row-save"
                              onClick={validateAndSaveEdit}
                              disabled={updateMut.isPending}
                              title="Save"
                            >
                              {updateMut.isPending ? '…' : '✓'}
                            </button>
                            <button
                              type="button"
                              className="pcp-row-del"
                              onClick={() => setConfirmDeleteId(r.id)}
                              title="Delete resident"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  }
                  return (
                    <tr key={r.id} className="pcp-row">
                      <td className="pcp-td pcp-td--name">
                        <span className="pcp-resident-name">{r.first_name} {r.last_name}</span>
                        {r.room ? <span className="pcp-resident-room">Rm {r.room}</span> : null}
                      </td>
                      {needsFacilityPick && (
                        <td className="pcp-td">
                          <span className="pcp-facility-name">{facilityMap.get(r.facility_id) ?? '—'}</span>
                        </td>
                      )}
                      <td className="pcp-td">
                        {r.pcp_name
                          ? <span className="pcp-doctor">{r.pcp_name}</span>
                          : <span className="pcp-empty-cell">—</span>}
                      </td>
                      <td className="pcp-td">
                        {r.pcp_fax
                          ? <span className="pcp-fax mono">{r.pcp_fax}</span>
                          : <span className="pcp-empty-cell">—</span>}
                      </td>
                      <td className="pcp-td pcp-td--actions">
                        <div className="pcp-row-actions">
                          <button
                            type="button"
                            className="pcp-action-btn pcp-action-btn--edit"
                            onClick={() => startEdit(r)}
                            title="Edit resident"
                          >
                            <PencilIcon />
                          </button>
                          <button
                            type="button"
                            className="pcp-action-btn pcp-action-btn--del"
                            onClick={() => setConfirmDeleteId(r.id)}
                            title="Delete resident"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}

                {showAddRow && (
                  <tr className="pcp-row pcp-row-edit">
                    <td className="pcp-td">
                      <input
                        ref={addNameRef}
                        className="pcp-inline-input"
                        placeholder="Resident name"
                        value={addForm.name}
                        onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                        onKeyDown={handleAddKey}
                      />
                    </td>
                    {needsFacilityPick && (
                      <td className="pcp-td">
                        <select
                          className="pcp-inline-select"
                          value={addFacilityId}
                          onChange={(e) => setAddFacilityId(e.target.value)}
                        >
                          <option value="" disabled>— facility —</option>
                          {facilities.map((f) => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="pcp-td">
                      <input
                        className="pcp-inline-input"
                        placeholder="PCP name"
                        value={addForm.pcp_name}
                        onChange={(e) => setAddForm((f) => ({ ...f, pcp_name: e.target.value }))}
                        onKeyDown={handleAddKey}
                      />
                    </td>
                    <td className="pcp-td pcp-td-fax-edit">
                      <div className="pcp-fax-edit-row">
                        <div className="pcp-fax-input-wrap">
                          <input
                            className={'pcp-inline-input' + (addForm.faxErr ? ' pcp-inline-input--err' : '')}
                            placeholder="Fax number"
                            value={addForm.pcp_fax}
                            onChange={(e) => {
                              const v = formatFax(e.target.value)
                              setAddForm((f) => ({ ...f, pcp_fax: v, faxErr: '' }))
                            }}
                            onKeyDown={handleAddKey}
                          />
                          {addForm.faxErr && <span className="pcp-fax-err">{addForm.faxErr}</span>}
                        </div>
                        <button
                          type="button"
                          className="pcp-row-save"
                          onClick={validateAndSaveAdd}
                          disabled={createMut.isPending}
                          title="Save"
                        >
                          {createMut.isPending ? '…' : '✓'}
                        </button>
                        <button
                          type="button"
                          className="pcp-row-del"
                          onClick={() => { setShowAddRow(false); setAddForm(emptyRow) }}
                          title="Cancel"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="pcp-add-row">
              <button type="button" className="pcp-add-btn" onClick={handleAddResident}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add resident
              </button>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        size="sm"
        title="Delete resident?"
        subtitle={confirmResident ? `${confirmResident.first_name} ${confirmResident.last_name}` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDeleteId(null)} type="button">
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteMut.isPending}
              onClick={() => confirmDeleteId && deleteMut.mutate(confirmDeleteId)}
            >
              Yes, delete
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 'var(--size-body)', color: 'var(--text-secondary)', margin: 0 }}>
          This will permanently delete the resident and all their vitals history. This action cannot be undone.
        </p>
      </Modal>
    </Drawer>
  )
}

function PencilIcon(): React.JSX.Element {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )
}

function TrashIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  )
}
